import { motion } from "framer-motion";
import {
  FaBriefcase,
  FaBullseye,
  FaGraduationCap,
  FaTools,
  FaTrophy,
} from "react-icons/fa";

const cardVariants = {
  hidden: {
    opacity: 0,
    y: 30,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.55,
      ease: "easeOut",
    },
  },
};

const AboutCard = ({ icon, title, children }) => {
  return (
    <motion.article
      variants={cardVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{
        once: true,
        amount: 0.2,
      }}
      whileHover={{
        y: -4,
      }}
      className="rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm transition-shadow duration-300 hover:shadow-lg"
    >
      <div className="mb-5 flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-50 text-lg text-indigo-600">
          {icon}
        </div>

        <h3 className="text-xl font-semibold text-zinc-900">
          {title}
        </h3>
      </div>

      <div className="space-y-4 text-base leading-7 text-zinc-600">
        {children}
      </div>
    </motion.article>
  );
};

const skills = [
  "Python",
  "Java",
  "JavaScript",
  "TypeScript",
  "PHP",
  "React",
  "FastAPI",
  "Laravel",
  "SQL",
  "MySQL",
  "MongoDB",
  "AWS",
  "Docker",
  "Hadoop",
  "Git",
  "Machine Learning",
];

const About = ({ embedded = false }) => {
  return (
    <section
      id="about"
      className={`scroll-mt-20 bg-white px-6 sm:px-10 lg:px-16 ${
        embedded
          ? "py-24 lg:py-32"
          : "min-h-screen py-20"
      }`}
    >
      <div className="mx-auto max-w-7xl">
        {!embedded && (
          <motion.div
            initial={{
              opacity: 0,
              y: 24,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.6,
            }}
            className="mb-16 text-center"
          >
            <p className="text-sm font-semibold uppercase tracking-[0.24em] text-indigo-600">
              About me
            </p>

            <h1 className="mt-4 text-4xl font-semibold tracking-tight text-zinc-950 sm:text-5xl">
              A little more about my work and background
            </h1>
          </motion.div>
        )}

        <div className="grid items-center gap-14 lg:grid-cols-[0.85fr_1.15fr]">
          {/* Profile image */}
          <motion.div
            initial={{
              opacity: 0,
              x: -35,
            }}
            whileInView={{
              opacity: 1,
              x: 0,
            }}
            viewport={{
              once: true,
              amount: 0.2,
            }}
            transition={{
              duration: 0.65,
            }}
            className="relative"
          >
            <div className="absolute -inset-5 rounded-[2rem] bg-gradient-to-br from-indigo-100 to-sky-100" />

            <div className="relative overflow-hidden rounded-[2rem] border border-zinc-200 bg-zinc-100 shadow-xl shadow-zinc-300/30">
              <img
                src="/images/me.jpg"
                alt="Kacper Agatowski"
                className="h-[500px] w-full object-cover object-top"
              />

              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-zinc-950/85 via-zinc-950/20 to-transparent p-7 pt-28">
                <p className="text-lg font-semibold text-white">
                  Kacper Agatowski
                </p>

                <p className="mt-1 text-sm text-zinc-200">
                  Software developer based in Dublin
                </p>
              </div>
            </div>
          </motion.div>

          {/* Main introduction */}
          <motion.div
            initial={{
              opacity: 0,
              x: 35,
            }}
            whileInView={{
              opacity: 1,
              x: 0,
            }}
            viewport={{
              once: true,
              amount: 0.2,
            }}
            transition={{
              duration: 0.65,
            }}
          >
            {embedded && (
              <p className="text-sm font-semibold uppercase tracking-[0.24em] text-indigo-600">
                About me
              </p>
            )}

            <h2 className="mt-4 text-4xl font-semibold leading-tight tracking-tight text-zinc-950 sm:text-5xl">
              I enjoy building reliable systems and
              understanding how they work beneath the
              interface.
            </h2>

            <div className="mt-7 space-y-5 text-lg leading-8 text-zinc-600">
              <p>
                I’m a recent MSc Computer Science graduate
                from University College Dublin, where my studies
                covered software engineering, cloud
                computing, information security, ethical
                hacking, relational databases, data
                science, machine learning, and information
                visualisation.
              </p>

              <p>
                My main interests are backend development,
                API design, databases, cloud infrastructure,
                cybersecurity, distributed systems, and
                applied AI. I particularly enjoy working on
                the parts of software responsible for data,
                business logic, reliability, authentication,
                and communication between services.
              </p>

              <p>
                My recent projects include RosterReady, an
                automated employee scheduling system built
                with FastAPI, React, MySQL, AWS, and a
                constraint satisfaction algorithm, as well
                as an AI-assisted assessment platform using
                generative AI and Bloom’s Taxonomy
                classification.
              </p>
            </div>

            {/* Skills */}
            <div className="mt-9 flex flex-wrap gap-3">
              {skills.map((skill, index) => (
                <motion.span
                  key={skill}
                  initial={{
                    opacity: 0,
                    y: 10,
                  }}
                  whileInView={{
                    opacity: 1,
                    y: 0,
                  }}
                  viewport={{
                    once: true,
                  }}
                  transition={{
                    duration: 0.3,
                    delay: index * 0.035,
                  }}
                  className="rounded-full border border-zinc-200 bg-zinc-50 px-4 py-2 text-sm font-medium text-zinc-700"
                >
                  {skill}
                </motion.span>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Information cards */}
        <div className="mt-24 grid gap-6 md:grid-cols-2">
          <AboutCard
            icon={<FaTools />}
            title="Technical experience"
          >
            <p>
              I work across backend and full-stack
              development using Python, Java, JavaScript,
              TypeScript, PHP, React, FastAPI, Laravel,
              Node.js, and relational and document
              databases.
            </p>

            <p>
              My wider experience includes REST API design,
              role-based access control, WebSockets,
              automated testing, Docker, Linux, Git
              workflows, CI/CD, AWS services, Hadoop,
              MapReduce, HDFS, and data visualisation.
            </p>
          </AboutCard>

          <AboutCard
            icon={<FaGraduationCap />}
            title="Education"
          >
            <p>
              I’m a recent MSc Computer Science graduate
              from University College Dublin, with modules
              covering software engineering, security,
              cloud computing, databases, machine learning,
              data science, and information visualisation.
            </p>

            <p>
              I graduated from the Institute of Art, Design
              and Technology in 2025 with a First Class
              Honours BSc in Creative Computing.
            </p>
          </AboutCard>

          <AboutCard
            icon={<FaBriefcase />}
            title="Professional experience"
          >
            <p>
              I worked as a Leaving Certificate Computer
              Science grinds teacher, delivering one-to-one
              lessons in programming fundamentals,
              algorithms, problem solving, and exam
              preparation.
            </p>

            <p>
              I have also worked in a customer-facing role
              since 2021, developing reliability,
              discretion, communication, cash-handling, and
              operational reporting skills.
            </p>
          </AboutCard>

          <AboutCard
            icon={<FaBullseye />}
            title="What I’m looking for"
          >
            <p>
              I’m looking for a graduate software
              engineering role where I can contribute to
              production systems, learn from experienced
              engineers, and continue improving my
              technical judgement.
            </p>

            <p>
              I’m particularly interested in backend,
              full-stack, cloud, platform, security, and
              infrastructure-focused teams.
            </p>
          </AboutCard>

          <AboutCard
            icon={<FaTrophy />}
            title="Achievements"
          >
            <p>
              I achieved First Class Honours in my BSc
              Creative Computing degree and received the
              IADT Football Player of the Year award in
              2025.
            </p>

            <p>
              I also hold a PADI Open Water Diver
              certification.
            </p>
          </AboutCard>

          <AboutCard
            icon={<FaTrophy />}
            title="Outside software"
          >
            <p>
              Outside technology, my main interests include
              football, rugby, golf, diving, and keeping
              active.
            </p>

            <p>
              Playing team sports has strengthened my
              communication, consistency, accountability,
              and ability to work toward shared goals.
            </p>
          </AboutCard>
        </div>
      </div>
    </section>
  );
};

export default About;
